import React, { useCallback, useEffect, useState } from 'react'
import { LinkButton } from '@adsk/alloy-react-button'
import { Box, Flex, Grid, Text } from '@adsk/alloy-react-box'
import FormField from '@adsk/alloy-react-form-field'
import { PencilFilledIcon, PlusIcon } from '@adsk/alloy-react-icon'
import TextInput from '@adsk/alloy-react-text-input'
import Theme from '@adsk/alloy-react-theme'
import { LINK_CONSTANTS, STATIC_IMAGES } from '../../constants'
import {
	SUPPORTED_VALIDATION_KEYS,
	validateInputField,
} from '../../utils/validation'
import {
	Avatar,
	device,
	ModalContextProps,
	NAPIAvatar,
	PhoneInput,
	type PhoneRef,
	useModalContext,
	HighZIndexInfoPopOver,
} from '@adsk/napi-shared'
import _ from 'lodash'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../store'
import styled from 'styled-components'
import { PROFILE_PICTURE_STEP_IDX } from '../../steps/profile-picture'
import MarketingComponent from '../marketing/MarketingComponent'
import { setSubmitState, setUserInfo } from '../../store/actions'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { getPackageInfo } from '../../utils'
import { getInitials } from '../../utils/helpers'
import { useTranslation } from '../../../i18n'
import ConditionalFlyoutComponent from '../ConditionalFlyoutComponent'
import JobSelectionComponent from './JobSelectionComponent'
import { InfoPopover } from '@adsk/alloy-react-tooltip'

const CameraDiv = styled.div`
	position: absolute;
	cursor: pointer;
	bottom: 0;
	right: 5%;
	@media${device.mobileXL} {
		right: 10%;
	}
`

const ErrorSpan = styled.div`
	padding-top: 2% !important;
`
const NAPIAvatarDiv = styled.div`
	position: relative;
	//Div size matching the Avatar Size to avoid the layout shift
	height: 128px;
	width: 128px;
`
const EditProfileComponent: React.FC = (): JSX.Element => {
	const { version, name } = getPackageInfo()
	const { t } = useTranslation()
	const phoneInputRef = React.useRef<PhoneRef>(null)
	const { typography, colors, spacing } = Theme
	const { jumpTo } = useModalContext() as ModalContextProps
	const {
		dispatch,
		state: { userInfo, selectionsList, marketingCompanyType, config, flowType },
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const [phone, setPhone] = useState<string>(userInfo?.phone ?? '')
	const prePopulatedCompanyName = _.get(
		userInfo,
		'companyName',
		''
	) as unknown as string

	const [invalidFields, setInvalidFields] = useState<string[]>([])

	const addInvalidFields = useCallback((fields: string[]) => {
		setInvalidFields((prevState) => [...prevState, ...fields])
	}, [])

	const resetInvalidFields = useCallback(() => {
		setInvalidFields([])
	}, [])

	const dispatchSubmitState = useCallback(
		(submitState: boolean) => {
			dispatch(setSubmitState(submitState))
		},
		[dispatch]
	)

	const fetchValidationFields = () => {
		return {
			...(!userInfo?.lockedFields?.includes('firstName') && {
				firstName: userInfo?.firstName,
			}),
			...(!userInfo?.lockedFields?.includes('lastName') && {
				lastName: userInfo?.lastName,
			}),
			...(!userInfo?.lockedFields?.includes('companyName') && {
				companyName: prePopulatedCompanyName,
			}),
			title: userInfo?.title,
		}
	}

	const isContactNumberValid = (): boolean => {
		if (phone && !phoneInputRef?.current?.isValidPhone()) {
			addInvalidFields(['phone'])
			return false
		}

		return phoneInputRef?.current?.isValidExtension() as unknown as boolean
	}

	const validateForm = useCallback(() => {
		let isValid = true
		resetInvalidFields()
		const validationFields = fetchValidationFields()
		// const lengthValidationFields = validationFields
		const invalidFields = Object.keys(
			_.pickBy(validationFields, (v, key) => {
				if (_.isEmpty(v)) return true
				const { isValid } = validateInputField(
					key as SUPPORTED_VALIDATION_KEYS,
					v
				)
				return !isValid
			})
		)
		if (!_.isEmpty(invalidFields)) {
			addInvalidFields(Array.from(invalidFields))
			isValid = false
		}
		if (!isContactNumberValid()) {
			isValid = false
		}

		if (!userInfo?.lockedFields?.includes('companyType')) {
			const selectedCompanyType = Object.keys(marketingCompanyType).filter(
				(key) => marketingCompanyType[key]
			)
			if (selectedCompanyType.length === 0) {
				addInvalidFields(['companyType'])
				isValid = false
			}
		}

		dispatchSubmitState(isValid)
		if (isValid) {
			dispatch(
				setUserInfo({
					...userInfo,
					phone: phoneInputRef?.current?.formatPhoneWithExtension(),
				})
			)
		}
	}, [
		userInfo?.firstName,
		userInfo?.lastName,
		prePopulatedCompanyName,
		addInvalidFields,
		dispatchSubmitState,
		phone,
		resetInvalidFields,
		userInfo?.title,
		marketingCompanyType,
	])

	const lockedFields = _.get(userInfo, 'lockedFields', []) as string[]

	const displayNameErrorComponent = (
		key: SUPPORTED_VALIDATION_KEYS,
		value?: string
	) => {
		const validationFields = fetchValidationFields()
		if (!_.isEmpty(value) && _.has(validationFields, key)) {
			const { isValid, errorKey } = validateInputField(key, value)
			if (!isValid) {
				return (
					<ErrorSpan data-testid={'invalid-name-error-testid'}>
						{t(errorKey)}
					</ErrorSpan>
				)
			}
		}
		return null
	}

	const AvatarActionComponent = (): JSX.Element => {
		return (
			<CameraDiv>
				{!_.isEmpty(userInfo?.updatedAvatarUrl) ? (
					<PencilFilledIcon
						data-testid={'cameraIcon'}
						color={colors.white}
						style={{
							backgroundColor: colors.adskBlue500,
							padding: 2,
						}}
						onClick={() => jumpTo(PROFILE_PICTURE_STEP_IDX)}
					/>
				) : (
					<PlusIcon
						data-testid={'cameraIcon'}
						color={colors.white}
						style={{
							backgroundColor: colors.adskBlue500,
							padding: 2,
						}}
						onClick={() => jumpTo(PROFILE_PICTURE_STEP_IDX)}
					/>
				)}
			</CameraDiv>
		)
	}
	useEffect(() => {
		validateForm()
	}, [validateForm])
	return (
		<React.Fragment>
			<Grid
				data-testid={'profileComponent'}
				gridGap={[2, 2]}
				padding={[15, 15]}
				paddingLeft={[15, 15]}
				paddingRight={[15, 35]}
				style={{
					backgroundColor: colors.adskBlue100,
				}}
				gridAutoColumns={'auto'}
				gridTemplateColumns={['auto', '25% auto']}
			>
				<Grid gridGap={2} justifyContent={'center'}>
					<NAPIAvatarDiv>
						<NAPIAvatar
							style={{
								alignSelf: 'center',
								cursor: 'pointer',
							}}
							showPlaceHolder={true}
							placeHolder={{
								type: 'ProgressRing',
								size: 'small',
								styles: {
									alignSelf: 'center',
								},
							}}
							fallbackImage={STATIC_IMAGES.PLACEHOLDER}
							onClick={() => jumpTo(PROFILE_PICTURE_STEP_IDX)}
							role={'button'}
							name={getInitials(`${userInfo?.firstName} ${userInfo?.lastName}`)}
							size={Avatar.SIZES.XXLARGE_128}
							image={userInfo?.updatedAvatarUrl}
							actionComponent={<AvatarActionComponent />}
						/>
					</NAPIAvatarDiv>
				</Grid>
				<Grid gridGap={2}>
					<Flex flexDirection={'row'}>
						<Flex flex={'50%'} marginRight={1}>
							<FormField
								label={t('FIRST_NAME_LABEL')}
								labelVariant={FormField.LABEL_VARIANTS.TOP}
								required
								errorVariant={'caption'}
								error={displayNameErrorComponent(
									'firstName',
									userInfo?.firstName
								)}
							>
								<ConditionalFlyoutComponent
									message={t('TOOL_TIP_NAME_DISABLED_MESSAGE')}
									show={lockedFields.includes('firstName')}
								>
									<TextInput
										type={TextInput.TYPES.TEXT}
										value={userInfo?.firstName}
										required
										data-testid={'firstNameField'}
										error={
											!_.isEmpty(userInfo?.firstName) &&
											invalidFields.includes('firstName')
										}
										disabled={lockedFields.includes('firstName')}
										placeholder={`${t('PLACEHOLDER_FNAME')}`}
										onChange={(e) => {
											track(
												ANALYTICS_TYPES.NAPI_USERS_MODIFIED_ONBOARDED_INFO,
												{
													package: name,
													version,
													flowType,
													fieldName: 'firstName',
												}
											)
											dispatch(
												setUserInfo({
													...userInfo,
													firstName: e.target.value,
												})
											)
										}}
									/>
								</ConditionalFlyoutComponent>
							</FormField>
						</Flex>
						<Flex flex={'50%'} marginLeft={1}>
							<FormField
								label={t('LAST_NAME_LABEL')}
								required
								labelVariant={FormField.LABEL_VARIANTS.TOP}
								errorVariant={'caption'}
								error={displayNameErrorComponent(
									'lastName',
									userInfo?.lastName
								)}
							>
								<ConditionalFlyoutComponent
									message={t('TOOL_TIP_NAME_DISABLED_MESSAGE')}
									show={lockedFields.includes('lastName')}
								>
									<TextInput
										type={TextInput.TYPES.TEXT}
										value={userInfo?.lastName}
										data-testid={'lastNameField'}
										disabled={lockedFields.includes('lastName')}
										error={
											!_.isEmpty(userInfo?.lastName) &&
											invalidFields.includes('lastName')
										}
										placeholder={`${t('PLACEHOLDER_LNAME')}`}
										onChange={(e) => {
											track(
												ANALYTICS_TYPES.NAPI_USERS_MODIFIED_ONBOARDED_INFO,
												{
													package: name,
													version,
													flowType,
													fieldName: 'lastName',
												}
											)
											dispatch(
												setUserInfo({
													...userInfo,
													lastName: e.target.value,
												})
											)
										}}
									/>
								</ConditionalFlyoutComponent>
							</FormField>
						</Flex>
					</Flex>
					<PhoneInput
						ref={phoneInputRef}
						accessedRegion={config?.REGION ?? 'US'}
						label={{
							value: t('PHONE_NUMBER_LABEL'),
							position: FormField.LABEL_VARIANTS.TOP,
						}}
						value={phone}
						updatePhone={setPhone}
						error={invalidFields.includes('phone')}
						errorMessage={t('PHONE_ERR_MESSAGE')}
						onValidInput={() => {
							dispatch(
								setUserInfo({
									...userInfo,
									phone: phoneInputRef?.current?.formatPhoneWithExtension(),
								})
							)
						}}
						extension={{
							labelValue: t('PHONE_EXTENSION_LABEL'),
							labelPos: FormField.LABEL_VARIANTS.TOP,
							errorMessage: t('TOOL_TIP_EXTENSION_ERROR_MESSAGE'),
							onValidInput: validateForm,
							onErrorInput: validateForm,
						}}
					/>
					<Box justifyContent={'center'}>
						<FormField
							required
							label={`${t('EMAIL_LABEL')}`}
							labelVariant={FormField.LABEL_VARIANTS.TOP}
						>
							<ConditionalFlyoutComponent
								message={`${t('TOOL_TIP_EMAIL_DISABLED_MESSAGE')} ${t(
									'ADSK_ACC'
								)}.`}
								show={lockedFields.includes('email')}
								placement={InfoPopover.PLACEMENTS.RIGHT}
							>
								<TextInput
									type={TextInput.TYPES.EMAIL}
									placeholder={`${t('PLACEHOLDER_EMAIL')}*`}
									data-testid={'emailField'}
									value={userInfo?.email}
									disabled={lockedFields.includes('email')}
									readOnly={true}
								/>
							</ConditionalFlyoutComponent>
						</FormField>
						<Text style={typography.caption}>
							<b style={{ paddingLeft: spacing.xxxSmall }}>
								{t('EMAIL_DISABLED_FIELD_INFO')}{' '}
							</b>
							<LinkButton
								onClick={() => {
									track(ANALYTICS_TYPES.NAPI_USERS_CLICKED_STATIC_LINK, {
										package: name,
										version,
										action: 'autodesk_account_link_clicked',
										linkText: 'Autodesk Account',
										link: config?.AUTODESK_PROFILE_ACCOUNT,
									})
									window.open(config?.AUTODESK_PROFILE_ACCOUNT, '_blank')
								}}
								size={LinkButton.SIZES.SMALL}
								style={{ fontWeight: 'bold' }}
							>
								{t('ADSK_ACC')}{' '}
								<HighZIndexInfoPopOver
									message={
										<div
											data-testid="napi-onboarding-user-tootip-msg"
											style={{ textAlign: 'justify' }}
										>
											<p>{t('ADSK_ACC')} </p>
											<p>
												{t('ADSK_ACC_TOOLTIP')}{' '}
												{LINK_CONSTANTS.AUTODESK_PROFILE_ACCOUNT_DOMAIN(
													config?.AUTODESK_PROFILE_ACCOUNT ??
														LINK_CONSTANTS.DEFAULT_AUTODESK_PROFILE_ACCOUNT
												)}
												{'.'}
											</p>
										</div>
									}
								/>
							</LinkButton>
						</Text>
					</Box>
					{selectionsList.length > 0 && (
						<JobSelectionComponent selectionsList={selectionsList} />
					)}
				</Grid>
			</Grid>
			<MarketingComponent
				displayNameErrorComponent={displayNameErrorComponent}
				invalidFields={invalidFields}
			/>
		</React.Fragment>
	)
}

export default EditProfileComponent
